// Página da tarefa "Faturamento dos últimos 12 meses" (Plano de ação › Ciclo 1 › Semana 1).
// Total, por setor e por cliente, mês a mês. Os números são os da Receita bruta da DRE:
// a soma dos setores é igual à soma dos clientes e igual ao total (o servidor confere).
// Rota: GET /financeiro/faturamento-12m (dre-engine). Contas puras em dadosSetores.ts.
import { useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Empty, Segmented, Space, Table, Tag, Tooltip as TipAntd, Typography } from 'antd';
import { Bar, BarChart, CartesianGrid, Legend, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { usePerms } from '../../../hooks/usePerms';
import { metaSetor } from '../../../utils/setoresDre';
import {
  buscarFaturamento12m, ErroDre, type Agrupar, type FatCliente, type Faturamento12m as Resposta,
  type PropsPagina, type TipoGrupoCliente,
} from './apiDre';
import {
  clientesTop, linhasFaturamento, pctTexto, periodoDeHoje, porSetorCanonico, reais, reaisCurto,
  rotuloMes, rotuloMesCurto, setoresDaResposta,
} from './dadosSetores';
import { AcessoNaoConferido, Carregando, EtiquetaSetor, IrPara, LINK_AREAS, SemAcesso, TooltipEmpilhado } from './comumDre';

const { Text } = Typography;
const PERM = 'financeiro.dre.ver';

/** Texto de um contrato da lista do cliente (o servidor pode mandar só o número ou um objeto). */
function rotuloContrato(c: any): string {
  if (c == null) return '';
  if (typeof c === 'string' || typeof c === 'number') return String(c);
  return String(c.numero || c.contrato_numero || c.codigo || c.id || '');
}

interface LinhaCliente {
  key: string; posicao: number | null; nome: string; total: number; pct: number; pctAcumulado: number | null;
  cliente: FatCliente | null; demais: boolean;
}

export default function Faturamento12m({ podeVer, hoje }: PropsPagina) {
  const { pode, carregando: carregandoPerms, erro: erroPerms, recarregar } = usePerms();
  const liberado = podeVer && pode(PERM);

  const [janela, setJanela] = useState<'fechados' | 'atual'>('fechados');
  const [agrupar, setAgrupar] = useState<Agrupar>('setores');
  const [grupo, setGrupo] = useState<TipoGrupoCliente>('cliente');
  const [visao, setVisao] = useState<'grafico' | 'tabela'>('grafico');
  const [dados, setDados] = useState<Resposta | null>(null);
  const [carregando, setCarregando] = useState(false);
  const [erro, setErro] = useState<ErroDre | null>(null);
  const pedido = useRef(0);

  useEffect(() => {
    if (!liberado) return;
    const meu = ++pedido.current;
    setCarregando(true); setErro(null);
    buscarFaturamento12m({ ate: janela === 'atual' ? periodoDeHoje(hoje) : undefined, agrupar, grupo })
      .then((d) => { if (meu === pedido.current) setDados(d); })
      .catch((e) => { if (meu === pedido.current) setErro(e instanceof ErroDre ? e : new ErroDre(String(e?.message || e), null)); })
      .finally(() => { if (meu === pedido.current) setCarregando(false); });
  }, [liberado, janela, agrupar, grupo, hoje]);

  const setores = useMemo(
    () => (dados ? setoresDaResposta(dados.setores, [...dados.meses.map((m) => m.por_setor), ...dados.por_setor.map((p) => ({ [p.setor]: p.valor }))]) : []),
    [dados],
  );
  const linhas = useMemo(() => (dados ? linhasFaturamento(dados.meses, setores) : []), [dados, setores]);
  const { top, demais } = useMemo(() => clientesTop(dados?.clientes || [], 15), [dados]);
  const foraDaDre = useMemo(() => (dados ? dados.meses.filter((m) => Math.abs(m.fora_da_dre || 0) >= 0.01) : []), [dados]);
  const porSetorTotal = useMemo(() => {
    const mapa = dados ? porSetorCanonico(Object.fromEntries(dados.por_setor.map((p) => [p.setor, p.valor]))) : {};
    const pcts = dados ? porSetorCanonico(Object.fromEntries(dados.por_setor.map((p) => [p.setor, p.pct]))) : {};
    return setores.map((s) => ({ setor: s, valor: mapa[s] || 0, pct: pcts[s] || 0 })).filter((x) => Math.abs(x.valor) >= 0.5);
  }, [dados, setores]);

  if (carregandoPerms) return <Carregando />;
  if (!liberado) return podeVer && erroPerms ? <AcessoNaoConferido onTentar={recarregar} /> : <SemAcesso />;

  const controles = (
    <Space wrap size={[16, 8]} style={{ marginBottom: 12 }}>
      <Segmented
        value={janela} onChange={(v) => setJanela(v as 'fechados' | 'atual')}
        options={[{ value: 'fechados', label: '12 meses fechados' }, { value: 'atual', label: 'Incluir mês atual' }]}
      />
      <Segmented
        value={agrupar} onChange={(v) => setAgrupar(v as Agrupar)}
        options={[{ value: 'setores', label: '6 setores' }, { value: 'frentes', label: '5 frentes' }]}
      />
      <Segmented
        value={grupo} onChange={(v) => setGrupo(v as TipoGrupoCliente)}
        options={[{ value: 'cliente', label: 'Cliente' }, { value: 'cnpj_raiz', label: 'Grupo (CNPJ raiz)' }]}
      />
    </Space>
  );

  if (erro && !dados) {
    return (
      <div>
        {controles}
        {erro.status === 403 ? <SemAcesso /> : <Alert type="error" showIcon message="Não foi possível carregar o faturamento" description={erro.message} />}
      </div>
    );
  }
  if (!dados) return <div>{controles}<Carregando texto="Somando as parcelas dos últimos 12 meses..." /></div>;

  const semSetor = dados.avisos?.sem_setor;
  const naoIdent = dados.avisos?.nao_identificados;
  const dadosGrafico = linhas.map((l) => ({ ...l, label: l.parcial ? `${l.label}*` : l.label }));
  const temParcial = dados.meses.some((m) => m.parcial);
  const periodos = dados.meses.map((m) => m.periodo);

  const colunasMes = [
    { title: 'Setor', dataIndex: 'setor', key: 'setor', fixed: 'left' as const, render: (s: string) => <EtiquetaSetor chave={s} /> },
    ...dados.meses.map((m) => ({
      title: rotuloMesCurto(m.periodo) + (m.parcial ? '*' : ''), key: m.periodo, align: 'right' as const,
      render: (_: any, r: any) => reais(r.meses[m.periodo] || 0),
    })),
    { title: 'Total', key: 'total', align: 'right' as const, render: (_: any, r: any) => <Text strong>{reais(r.total)}</Text> },
    { title: '%', key: 'pct', align: 'right' as const, render: (_: any, r: any) => pctTexto(r.pct) },
  ];
  const linhasMes = porSetorTotal.map((p) => ({
    key: p.setor, setor: p.setor, total: p.valor, pct: p.pct,
    meses: Object.fromEntries(linhas.map((l) => [l.periodo, Number(l[p.setor]) || 0])),
  }));

  const colunasSetor = [
    { title: 'Setor', dataIndex: 'setor', key: 'setor', render: (s: string) => <EtiquetaSetor chave={s} /> },
    { title: 'Total', dataIndex: 'valor', key: 'valor', align: 'right' as const, render: (v: number) => reais(v) },
    { title: '%', dataIndex: 'pct', key: 'pct', align: 'right' as const, render: (v: number) => pctTexto(v) },
  ];

  const linhasClientes: LinhaCliente[] = [
    ...top.map((c) => ({ key: c.chave, posicao: c.posicao, nome: c.nome, total: c.total, pct: c.pct, pctAcumulado: c.pct_acumulado, cliente: c, demais: false })),
    ...(demais ? [{ key: '__demais', posicao: null, nome: `Demais (${demais.n})`, total: demais.total, pct: demais.pct, pctAcumulado: null, cliente: null, demais: true }] : []),
  ];

  const colunasClientes = [
    { title: '#', key: 'pos', width: 48, render: (_: any, r: LinhaCliente) => r.posicao ?? '' },
    {
      title: dados.grupo === 'cnpj_raiz' ? 'Grupo' : 'Cliente', key: 'nome',
      render: (_: any, r: LinhaCliente) => (
        <span>
          {r.demais ? <Text type="secondary">{r.nome}</Text> : r.nome}
          {r.cliente && !r.cliente.identificado ? (
            <TipAntd title="Não achamos este cliente no cadastro; o nome é o que veio escrito no lançamento.">
              <Tag color="orange" style={{ marginLeft: 6 }}>não identificado</Tag>
            </TipAntd>
          ) : null}
        </span>
      ),
    },
    { title: 'Total', key: 'total', align: 'right' as const, render: (_: any, r: LinhaCliente) => reais(r.total) },
    { title: '%', key: 'pct', align: 'right' as const, render: (_: any, r: LinhaCliente) => pctTexto(r.pct) },
    { title: '% acumulado', key: 'acum', align: 'right' as const, render: (_: any, r: LinhaCliente) => pctTexto(r.pctAcumulado) },
    {
      title: 'Setores', key: 'setores',
      render: (_: any, r: LinhaCliente) => {
        if (!r.cliente) return null;
        const ps = Object.entries(porSetorCanonico(r.cliente.por_setor)).filter(([, v]) => Math.abs(v) >= 0.5).sort((a, b) => b[1] - a[1]);
        return (
          <Space size={[8, 2]} wrap>
            {ps.slice(0, 3).map(([s]) => <EtiquetaSetor key={s} chave={s} />)}
            {ps.length > 3 ? <Text type="secondary">+{ps.length - 3}</Text> : null}
          </Space>
        );
      },
    },
    {
      title: 'Contratos', key: 'contratos',
      render: (_: any, r: LinhaCliente) => {
        const lista = (r.cliente?.contratos || []).map(rotuloContrato).filter(Boolean);
        if (!lista.length) return null;
        return <TipAntd title={lista.join(', ')}><span>{lista.length}</span></TipAntd>;
      },
    },
  ];

  return (
    <div>
      {controles}

      <div style={{ marginBottom: 12 }}>
        <Text type="secondary">Faturamento de {rotuloMes(dados.de)} a {rotuloMes(dados.ate)}</Text>
        <div style={{ fontSize: 28, fontWeight: 600, lineHeight: 1.2 }}>{reais(dados.total)}</div>
        {temParcial ? <Text type="secondary">* o último mês ainda está em andamento.</Text> : null}
      </div>

      <Space direction="vertical" size={8} style={{ width: '100%', marginBottom: 12 }}>
        {semSetor && (Math.abs(semSetor.valor) >= 0.01 || semSetor.parcelas > 0 || semSetor.manuais > 0) ? (
          <Alert
            type="warning" showIcon
            message={`${reais(semSetor.valor)} sem setor (${semSetor.parcelas} parcela(s)${semSetor.manuais ? ` e ${semSetor.manuais} lançamento(s) manual(is)` : ''})`}
            description={<span>Para corrigir: <IrPara para={LINK_AREAS}>Operacional › Contratos › Áreas e CNAE</IrPara> (lista “Parcelas sem setor”).</span>}
          />
        ) : null}
        {foraDaDre.length ? (
          <Alert
            type="info" showIcon
            message={`${reais(foraDaDre.reduce((s, m) => s + m.fora_da_dre, 0))} de receita antiga que a DRE não conta`}
            description={`Meses: ${foraDaDre.map((m) => `${rotuloMes(m.periodo)} (${reais(m.fora_da_dre)})`).join(', ')}. Estão no cadastro de recebimentos, mas sem setor, e por isso ficam fora do total acima.`}
          />
        ) : null}
        {naoIdent && naoIdent.n > 0 ? (
          <Alert
            type="info" showIcon
            message={`${naoIdent.n} cliente(s) sem cadastro identificado (${reais(naoIdent.valor)})`}
            description="Aparecem com o nome escrito no lançamento. Se o nome estiver escrito de dois jeitos, o mesmo cliente pode aparecer em duas linhas."
          />
        ) : null}
        {erro ? <Alert type="error" showIcon message="Não consegui atualizar" description={erro.message} /> : null}
      </Space>

      <div style={{ opacity: carregando ? 0.5 : 1, transition: 'opacity .2s' }}>
        <Space style={{ marginBottom: 8 }} wrap>
          <Text strong>Por setor, mês a mês</Text>
          <Segmented size="small" value={visao} onChange={(v) => setVisao(v as 'grafico' | 'tabela')} options={[{ value: 'grafico', label: 'Gráfico' }, { value: 'tabela', label: 'Tabela' }]} />
        </Space>

        {visao === 'grafico' ? (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16, alignItems: 'flex-start' }}>
            <div style={{ flex: '3 1 480px', minWidth: 0, height: 'clamp(280px, calc(100vh - 380px), 520px)' }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={dadosGrafico} stackOffset="sign" margin={{ top: 8, right: 8, left: 0, bottom: 4 }}>
                  <CartesianGrid stroke="#eee" strokeDasharray="3 3" vertical={false} />
                  <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 11 }} tickFormatter={reaisCurto} width={56} />
                  <Tooltip content={(p: any) => <TooltipEmpilhado active={p.active} payload={p.payload} label={p.label} rotuloTotal="Total do mês" />} />
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                  {setores.map((s) => {
                    const m = metaSetor(s);
                    return <Bar key={s} dataKey={s} name={m.rotulo} stackId="fat" fill={m.cor} stroke="#fff" strokeWidth={1} maxBarSize={48} />;
                  })}
                </BarChart>
              </ResponsiveContainer>
            </div>
            <div style={{ flex: '2 1 300px', minWidth: 0 }}>
              <Table size="small" pagination={false} columns={colunasSetor} dataSource={porSetorTotal.map((p) => ({ ...p, key: p.setor }))} />
            </div>
          </div>
        ) : linhasMes.length ? (
          <Table size="small" pagination={false} scroll={{ x: 'max-content' }} columns={colunasMes} dataSource={linhasMes} />
        ) : <Empty description="Sem faturamento no período" />}
      </div>

      <div style={{ marginTop: 24, opacity: carregando ? 0.5 : 1, transition: 'opacity .2s' }}>
        <Text strong>Por {dados.grupo === 'cnpj_raiz' ? 'grupo de empresas' : 'cliente'}</Text>
        <Text type="secondary"> (15 maiores; abra a linha para ver mês a mês)</Text>
        <Table
          style={{ marginTop: 8 }} size="small" pagination={false} scroll={{ x: 'max-content' }}
          columns={colunasClientes} dataSource={linhasClientes}
          expandable={{
            rowExpandable: (r) => !!r.cliente,
            expandedRowRender: (r) => {
              const c = r.cliente!;
              const meses = periodos.filter((p) => Math.abs(c.por_mes?.[p] || 0) >= 0.5);
              const contratos = (c.contratos || []).map(rotuloContrato).filter(Boolean);
              return (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px 24px' }}>
                  {meses.map((p) => (
                    <span key={p}><Text type="secondary">{rotuloMesCurto(p)}</Text> {reais(c.por_mes[p])}</span>
                  ))}
                  {c.membros && c.membros.length > 1 ? (
                    <div style={{ flexBasis: '100%' }}><Text type="secondary">Empresas do grupo: </Text>{c.membros.map((x) => x.nome).join('; ')}</div>
                  ) : null}
                  {contratos.length ? <div style={{ flexBasis: '100%' }}><Text type="secondary">Contratos: </Text>{contratos.join(', ')}</div> : null}
                </div>
              );
            },
          }}
        />
      </div>
    </div>
  );
}
